export interface EmailSignupConfig {
  formId: string
  endpoint: string
  tags?: string[]
  source?: string
  successMessage?: string
  errorMessage?: string
  onSuccess?: (response: EmailSignupResponse) => void
  onError?: (error: unknown) => void
  onSubmit?: (email: string) => void
}

export interface EmailSignupResponse {
  success: boolean
  message?: string
  errors?: Record<string, string[]>
}

export class EmailSignupService {
  private static instance: EmailSignupService | undefined
  private activeSubmissions = new Set<string>()

  public static getInstance(): EmailSignupService {
    if (!this.instance) {
      this.instance = new EmailSignupService()
    }
    return this.instance
  }

  public static createDrawerWidgetConfig(widgetId: string, contentConfig: Record<string, unknown>): EmailSignupConfig {
    const successMessageFromConfig = contentConfig.success_message

    return {
      formId: `drawer-widget-${widgetId}`,
      endpoint: '/api-root/newsletter-signup/',
      source: 'drawer_widget',
      successMessage:
        typeof successMessageFromConfig === 'string' && successMessageFromConfig.trim().length > 0
          ? successMessageFromConfig
          : 'Thank you for signing up!',
      errorMessage: 'Unable to process your subscription. Please try again.'
    }
  }

  public async submitSignup(email: string, config: EmailSignupConfig): Promise<EmailSignupResponse> {
    const submissionKey = `${config.formId}-${email}`
    if (this.activeSubmissions.has(submissionKey)) {
      return { success: false, message: 'Submission already in progress' }
    }

    const validation = this.validateEmail(email)
    if (!validation.valid) {
      return { success: false, message: validation.message }
    }

    this.activeSubmissions.add(submissionKey)
    config.onSubmit?.(email)

    try {
      const formData = this.buildFormData(email, config)
      const response = await this.makeRequest(config.endpoint, formData)

      if (response.success) {
        config.onSuccess?.(response)
        return {
          success: true,
          message: config.successMessage || response.message || 'Thank you for signing up!'
        }
      }

      config.onError?.(response)
      return {
        success: false,
        message: config.errorMessage || response.message || 'Something went wrong. Please try again.',
        errors: response.errors
      }
    } catch (error) {
      config.onError?.(error)
      return {
        success: false,
        message: 'Network error. Please try again later.'
      }
    } finally {
      this.activeSubmissions.delete(submissionKey)
    }
  }

  private validateEmail(email: string): {
    valid: boolean
    message?: string
  } {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return {
        valid: false,
        message: 'Please enter a valid email address.'
      }
    }

    return { valid: true }
  }

  private buildFormData(email: string, config: EmailSignupConfig): FormData {
    const formData = new FormData()
    formData.append('email', email)

    if (config.tags?.length) {
      formData.append('tags', JSON.stringify(config.tags))
    }

    if (config.source) {
      formData.append('source', config.source)
    }

    formData.append('form_id', config.formId)
    return formData
  }

  private async makeRequest(endpoint: string, formData: FormData): Promise<EmailSignupResponse> {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'X-CSRFToken': this.getCsrfToken()
      },
      body: formData
    })

    const payload = (await response.json().catch(() => ({}))) as EmailSignupResponse
    if (response.ok) {
      return payload
    }

    return {
      success: false,
      message: payload.message || `HTTP ${response.status}`,
      errors: payload.errors
    }
  }

  private getCsrfToken(): string {
    const name = 'csrftoken'
    const cookies = document.cookie.split(';')
    for (let cookie of cookies) {
      cookie = cookie.trim()
      if (cookie.startsWith(name + '=')) {
        return decodeURIComponent(cookie.substring(name.length + 1))
      }
    }

    const csrfInput = document.querySelector('input[name="csrfmiddlewaretoken"]') as HTMLInputElement | null
    return csrfInput?.value || ''
  }
}
